import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import * as rison from 'rison-node';
import {
  DEFAULT_SEARCH_STATE,
  SearchStateInputs,
  SearchStateService,
} from './search-state.service';
import { Enums } from '@cyborg-monorepo/hunter/common';

export interface CyborgRouterSearchLink {
  routerLink: string | string[];
  queryParams: Params;
}

@Injectable({ providedIn: 'root' })
export class SearchUrlService {
  constructor(
    private router: Router,
    private searchStateService: SearchStateService,
  ) {}

  /**
   * Navigate to the search results for the given search state.
   * @param state search state
   */
  public gotoUrl(state: Partial<SearchStateInputs>): void {
    const searchParams = this.stateToParams(state);
    this.router.navigate(['/research', 'search'], {
      queryParams: searchParams,
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Transform a search state into data for an angular routerLink
   * @param state search state
   * @returns {routerLink, queryParams}
   *    ei:
   *      <a [routerLink]="search.routerLink" [queryParams]="search.queryParams">
   */
  public stateToRouterLink(
    state: Partial<SearchStateInputs>,
  ): CyborgRouterSearchLink {
    const queryParams = this.stateToParams(state);
    return {
      routerLink: ['/research', 'search'],
      queryParams: queryParams,
    };
  }

  /**
   * Transform a rison query parameter into a search state.
   * @param query active router query params
   * @returns search state
   */
  public searchStateFromString(query: Params): SearchStateInputs {
    return {
      ...DEFAULT_SEARCH_STATE,
      ...rison.decode(query['state']),
    };
  }

  public stateToParams(state: Partial<SearchStateInputs>): Params {
    const targetState = { ...DEFAULT_SEARCH_STATE, ...state };

    const defaultKeys: string[] = Object.keys(DEFAULT_SEARCH_STATE);
    Object.keys(targetState).forEach((key) => {
      if (!defaultKeys.some((dkey) => dkey === key)) {
        delete targetState[key];
      }
    });

    return {
      state: rison.encode(targetState),
    };
  }

  public navigateToCollection(collection: string): void {
    this.searchStateService.reset();
    this.searchStateService.setLibrary(
      [Enums.CyborgIndexEnum.CYBORG_COLLECTIONS],
      true,
    );
    this.searchStateService.setTerm(collection);

    const searchLink: CyborgRouterSearchLink =
      this.stateToRouterLink(
        this.searchStateService.state.getValue(),
      );

    this.router.navigate(searchLink.routerLink as string[], {
      queryParams: searchLink.queryParams,
    });
  }
}
